import React from 'react';
import { Search, Eye, Navigation, Share2 } from 'lucide-react';

const HowItWorksSection = () => {
  const steps = [
    {
      icon: Search,
      title: "Trouvez facilement un spot",
      description: "Carte interactive avec tous les spots précis référencés par la communauté.",
      color: "from-blue-400 to-blue-600"
    },
    {
      icon: Eye,
      title: "Visualisez précisément le lieu",
      description: "Descriptions détaillées, photos réelles du lieu, avis et recommandations des utilisateurs.",
      color: "from-green-400 to-green-600"
    },
    {
      icon: Navigation,
      title: "Accédez au spot facilement",
      description: "Informations pratiques complètes (parking, accessibilité, temps d'accès depuis le parking).",
      color: "from-purple-400 to-purple-600"
    },
    {
      icon: Share2,
      title: "Partagez vos propres découvertes",
      description: "Ajoutez facilement vos spots favoris pour enrichir la communauté.",
      color: "from-amber-400 to-orange-600"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mb-6">
            Comment ça marche exactement ?
          </h2>
          <p className="text-xl text-amber-700 max-w-3xl mx-auto">
            4 étapes simples pour découvrir et partager vos spots préférés
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {steps.map((step, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
            >
              <div className="flex items-start space-x-6">
                {/* Step Number */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-amber-800 font-bold text-lg">{index + 1}</span>
                  </div>
                  <div className={`p-4 bg-gradient-to-br ${step.color} rounded-xl shadow-md`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-amber-900 mb-4 flex items-center">
                    {step.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Visual Flow */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-amber-900 mb-4">
              En résumé : Simple, rapide, communautaire
            </h3>
            <p className="text-gray-700 text-lg max-w-3xl mx-auto">
              Sun-Spot transforme la découverte de beaux lieux en une expérience simple et accessible. 
              Plus besoin de chercher pendant des heures : la communauté a déjà fait le travail pour vous.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
            <div className="text-center p-4 bg-white/60 backdrop-blur-sm rounded-xl">
              <div className="text-3xl mb-2">🗺️</div>
              <div className="font-semibold text-amber-800">Carte interactive</div>
            </div>
            <div className="text-amber-600 text-2xl hidden md:block">→</div>
            <div className="text-center p-4 bg-white/60 backdrop-blur-sm rounded-xl">
              <div className="text-3xl mb-2">📍</div>
              <div className="font-semibold text-amber-800">Spot précis</div>
            </div>
            <div className="text-amber-600 text-2xl hidden md:block">→</div>
            <div className="text-center p-4 bg-white/60 backdrop-blur-sm rounded-xl">
              <div className="text-3xl mb-2">🧘</div>
              <div className="font-semibold text-amber-800">Moment zen</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;